import { Trans } from 'react-i18next';
import './verifyOtp.css';
import TabletPinInput from '../../component/tabletPinInput/tabletPinInput';
import DesktopPinInput from '../../component/desktopPinInput/desktopPinInput';
import TabletBackButton from '../../component/tabletBackButton/tabletBackButton';
import DesktopBackButton from '../../component/desktopBackButton/desktopBackbutton';
import TabletLayoutImage from '../../component/tabletLayoutImage/tabletLayoutImage';
import DesktopLayoutImage from '../../component/desktopLayoutmage/desktopLayoutImage';
import Loading from '../../assets/loading/loading';
import { OTPLength } from '../../utils/helper.type';
import RenderInPlayLogo from '../../component/inPlayLogo/inPlayLogo';
import { getDataTestIdValue, getIdAttributes } from '../../utils/helper';

export const idPrefix: string = `verify-otp`;
/**
 * Renders the desktop view for the Forgot Password page.
 *
 * @param handleSubmit - Function to handle form submission
 * @param email - Current email state value
 * @param setEmail - Function to update email state
 * @param loginTranslation - Translation object for login fields
 * @param isDisabled - Boolean to disable the submit button
 * @param onClickLogin - Function to navigate back to login page
 * @returns JSX.Element
 */
export const renderDesktopView = (
  otp: string,
  setOtp: React.Dispatch<React.SetStateAction<string>>,
  onClickLogin: () => void,
  email: string,
  error: string | null,
  isVerifyLoading: boolean,
  handleResendClick: () => void,
) => {
  return (
    <div
      className="lg:flex h-screen w-full font-helvetica"
      {...getIdAttributes(idPrefix, 'desktop-view')}
    >
      <div
        className="flex flex-col flex-1 px-10 py-6"
        {...getIdAttributes(idPrefix, 'desktop-left')}
      >
        <DesktopBackButton
          onClick={onClickLogin}
          {...getIdAttributes(idPrefix, 'back-button')}
        />
        <div
          className="flex flex-col max-w-[520px] w-full mx-auto"
          {...getIdAttributes(idPrefix, 'desktop-container')}
        >
          <div
            className="mt-[200px]"
            {...getIdAttributes(idPrefix, 'otp-section')}
          >
            <RenderInPlayLogo {...getIdAttributes(idPrefix, 'inplay-logo')} />
            <div
              className="login-header-container mb-[20px]"
              {...getIdAttributes(idPrefix, 'otp-header')}
            >
              <h1
                className="text-6xl text-[#1A1A1A]"
                {...getIdAttributes(idPrefix, 'otp-title')}
              >
                <Trans i18nKey="verifyOtp.otpTitle" />
              </h1>
            </div>

            <p
              className="mb-6 text-[#1A1A1A] text-base"
              {...getIdAttributes(idPrefix, 'otp-description')}
            >
              <Trans i18nKey="verifyOtp.otpDescription" />
              {email && (
                <span
                  className="font-semibold ml-2"
                  {...getIdAttributes(idPrefix, 'user-email')}
                >
                  {email}
                </span>
              )}
            </p>

            {isVerifyLoading ? (
              <div
                className="mt-16 mb-16 justify-center flex"
                {...getIdAttributes(idPrefix, 'loading-indicator')}
              >
                <Loading width={30} height={30} />
              </div>
            ) : (
              <div
                className="mt-16 mb-16"
                {...getIdAttributes(idPrefix, 'otp-input-container')}
              >
                <DesktopPinInput
                  otp={otp}
                  setOtp={setOtp}
                  placeholder="0"
                  hasError={!!error}
                  inputLength={OTPLength}
                  {...getIdAttributes(idPrefix, 'otp-input')}
                />

                {error && (
                  <p
                    className="text-sm text-[#D80000] mt-3 ml-1"
                    {...getIdAttributes(idPrefix, 'otp-error')}
                  >
                    {error}
                  </p>
                )}
              </div>
            )}

            <div
              onClick={handleResendClick}
              className="underline text-sm text-[#000000] cursor-pointer"
              {...getIdAttributes(idPrefix, 'resend-link')}
            >
              <Trans i18nKey="resendOtp" />
            </div>
          </div>
        </div>
      </div>

      <DesktopLayoutImage {...getIdAttributes(idPrefix, 'layout-image')} />
    </div>
  );
};

/**
 * Renders the login view for tablet screens.
 *
 * This function provides a login form layout with a background image, form fields for email and password, and
 * supports toggling between login options. It also handles showing the "Forgot Password" modal when necessary.
 *
 * @param activeLoginKey - The currently active login option (e.g., login or sign-up).
 * @param handleToggle - Function to toggle between different login options.
 * @param email - The email entered by the user in the form.
 * @param setEmail - Function to update the email state.
 * @param password - The password entered by the user in the form.
 * @param setPassword - Function to update the password state.
 * @param loginTranslation - The translations for the login form fields.
 * @param isDisabled - Boolean indicating whether the form is disabled (e.g., during loading or validation).
 * @param isLoading - Boolean indicating whether the form is in a loading state.
 * @param handleSubmit - The function to handle form submission.
 * @param showPassword - Boolean indicating whether the password is visible or hidden.
 * @param setShowPassword - Function to toggle the visibility of the password.
 * @param isDesktop - Boolean indicating whether the device is a desktop (used for the "Forgot Password" modal).
 * @param setIsForgotModalOpen - Function to open the "Forgot Password" modal.
 *
 * @returns A JSX element representing the tablet login view.
 */
export const renderTabletView = (
  handleResendClick: () => void,
  pin: string,
  setPin: (pin: string) => void,
  otpLength: number,
  handleBackToLogin: () => void,
  authText: string,
  error: string | null,
  handleVerifyOtp?: () => void,
  isVerifyLoading?: boolean,
  email?: string,
) => {
  return (
    <div
      className="relative min-h-screen w-full font-helvetica"
      {...getIdAttributes(idPrefix, 'tablet-view')}
    >
      <TabletLayoutImage
        {...getIdAttributes(idPrefix, 'tablet-layout-image')}
      />
      <div
        className="absolute inset-0 bg-black/30"
        {...getIdAttributes(idPrefix, 'overlay')}
      />

      <TabletBackButton
        onClick={handleBackToLogin}
        {...getIdAttributes(idPrefix, 'back-button')}
      />
      <div
        className="tablet-login-container"
        {...getIdAttributes(idPrefix, 'tablet-container')}
      >
        <div className="flex flex-col">
          <div className="text-white mt-auto mb-3">
            <RenderInPlayLogo
              className={'mb-10 h-10'}
              {...getIdAttributes(idPrefix, 'inplay-logo')}
            />
            <h1
              className="otp-title"
              {...getIdAttributes(idPrefix, 'otp-title')}
            >
              <Trans i18nKey="verifyOtp.otpTitle" />
            </h1>
            <p
              className="mb-4 text-white text-base"
              {...getIdAttributes(idPrefix, 'otp-description')}
            >
              <Trans i18nKey="verifyOtp.otpDescription" />
              {email && (
                <span
                  className="font-semibold ml-2"
                  {...getIdAttributes(idPrefix, 'user-email')}
                >
                  {email}
                </span>
              )}
            </p>
          </div>
        </div>

        <div
          className="otp-input-container"
          {...getIdAttributes(idPrefix, 'otp-input-container')}
        >
          <div
            className="tablet-otp-container"
            {...getIdAttributes(idPrefix, 'tablet-otp-container')}
          >
            <div
              className="bg-white rounded-2xl p-2 w-full"
              {...getIdAttributes(idPrefix, 'otp-input-box')}
            >
              <TabletPinInput
                authText={authText}
                pin={pin}
                setPin={setPin}
                otpLength={otpLength}
                handleVerifyOtp={handleVerifyOtp}
                error={error}
                isVerifyLoading={isVerifyLoading}
                handleAuthClick={handleResendClick}
                {...getIdAttributes(idPrefix, 'tablet-pin-input')}
                forgotPinTestId={getDataTestIdValue(
                  idPrefix,
                  'forgot-pin-auth',
                )}
                otpErrorTestId={getDataTestIdValue(idPrefix, 'otp-error')}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
